import { useEffect, useRef } from 'react';

import { dampAngle } from '../../engine/math/mathx.js';
import { resolvePlayerMark, useBlipFeed } from '../hooks/useBlipFeed.js';
import {
  MAP_COLORS,
  MARKER_COLORS,
  ROT_OVERSCAN,
  applyViewRotation,
  blitBase,
  clampToEdge,
  drawBlip,
  drawEdgeArrow,
  drawMarkerLabel,
  drawMissionMarker,
  drawPlaceholder,
  drawPlayerMarker,
  getBase,
  inViewWindow,
  mapRotation,
  northScreenDir,
  roundRectPath,
  viewPoint,
  viewX,
  viewY,
} from './mapRender.js';

/**
 * @file The bottom-left minimap: a rounded-rect window onto the island, centred
 * on the player and ROTATED so that map-up is screen-up. The static base —
 * ocean, landmass, roads, districts — is blitted from the shared offscreen
 * bitmap in `mapRender.js`, which the pause menu's full map also uses. One
 * renderer, two consumers, and only this one turns: the full map is north-up
 * forever, so every rotation-aware thing in `mapRender.js` is opt-in behind
 * `view.rot` and the pause menu simply never sets it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT ROTATES WITH THE CAMERA, NOT WITH THE PLAYER
 * ─────────────────────────────────────────────────────────────────────────────
 * Owner decision, and it is the only choice that means anything here: this
 * game's yaw is FREE and MANUAL (hold Q/E, or drag), and the camera never
 * auto-follows the player's heading. Rotating with the PLAYER would leave the
 * minimap disagreeing with the screen every time the player walked sideways;
 * rotating with the CAMERA makes minimap-up and screen-up the same direction,
 * always, which is the whole promise a rotating minimap makes.
 *
 * The angle is `mapRotation(engine.yaw)` — see `mapRender.js`'s header for the
 * derivation and `tests/minimap.test.mjs` (a) for the proof that the sign is
 * not mirrored. The player chevron then keeps its world-absolute
 * `PI - yaw` inside the rotated context and comes out CAMERA-RELATIVE for
 * free: it points up exactly when the player faces the way the camera looks.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * FOUR THINGS ROTATION BREAKS, AND WHERE EACH IS FIXED
 * ─────────────────────────────────────────────────────────────────────────────
 *  • A FULL-WINDOW FILL SWEEPS ITS OWN CIRCUMCIRCLE. Anything that has to cover
 *    the window from inside the rotated context needs `ROT_OVERSCAN`, or the
 *    corners are bare wedges that swing round as you turn. The ocean backdrop
 *    dodges it by being painted BEFORE the rotation (it is a flat colour, so
 *    unrotated is identical and free); the placeholder grid cannot, so it is
 *    drawn over an overscanned rect with the pad folded into its scroll phase.
 *    The base BLIT needs nothing — `blitBase` draws the whole island bitmap and
 *    lets the clip window it, so there is no source rect to run out of.
 *  • THE OFF-SCREEN BLIP REJECT STOPS BEING EXACT. It was a box test because
 *    the window was an axis-aligned square. `inViewWindow` keeps that test at
 *    rot 0 and widens to the half-DIAGONAL when rotating — rotation preserves
 *    distance from the centre, so that can never drop a visible blip.
 *  • CHROME MUST NOT TURN. The bezel, the clip and the drop shadow are the
 *    frame, and a frame that rotates reads as a bug; a LETTER that rotates is
 *    unreadable. All of it is drawn after `ctx.restore()`, and the north badge
 *    ORBITS on `northScreenDir` while staying upright.
 *  • THE CAMERA-FORWARD TICK BECAME A LIE. Camera-forward is up now, by
 *    construction, so the old rim tick would have been pinned to the top
 *    forever. North took its place: on a rotating map that is the one bearing
 *    the player can no longer read off the frame.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS COMPONENT RE-RENDERS EXACTLY NEVER
 * ─────────────────────────────────────────────────────────────────────────────
 * `useBlipFeed` parks the 8 Hz channel in a ref, an rAF loop reads it and
 * paints, and React is out of the picture after mount. Putting blips through
 * `useEngineBus` would re-render the HUD tree eight times a second to move
 * pixels inside a canvas React does not own. The rotation lives in the same
 * `view` ref as everything else for the same reason — it is a number the
 * canvas reads, never a number the DOM does.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * DEGRADATION, IN THE ORDER IT HAPPENS
 * ─────────────────────────────────────────────────────────────────────────────
 *  • no `'hud:blips'` (dev scene, gameplay not installed, game paused): the
 *    player mark falls back to `engine.target` and the camera's own forward,
 *    and no blips are drawn. See `resolvePlayerMark`.
 *  • no `level.map` (every dev scene, and the city until the island lands): a
 *    dark grid placeholder that SCROLLS with the player and TURNS with the
 *    camera, so the HUD reads as "no map data" rather than as broken — a grid
 *    that stayed still while the world spun would read as a dead widget.
 *  • no engine: the placeholder, static, rotation 0.
 *
 * @param {Object} props
 * @param {import('../../engine/core/Engine.js').Engine|null} props.engine
 * @param {number} [props.size=220] CSS px, square.
 * @param {number} [props.scale=1.4] CSS px per world metre.
 */

/** Redraw ceiling. rAF drives the loop; this is the throttle. */
const REDRAW_MS = 50;

/**
 * Rotation follow stiffness, 1/s (`mathx.damp` lambda). 20 is one REDRAW_MS of
 * lag and it is deliberately the smallest damping that does anything at all:
 * `engine.yaw` arrives ALREADY smooth (`CameraRig` eases a Shift+Q/E snap over
 * `snapTime` 0.3 s and ramps a held spin through `spinAccel`), so the map needs
 * no help there and heavier damping would only make it trail the world during a
 * drag. What it is here for is the discontinuities the rig does not ease —
 * `setYaw` on level entry, a future scripted camera jump — which would
 * otherwise snap the whole map round in one frame. `dampAngle` also takes the
 * SHORT way round, so an angle that wraps can never unwind a full turn.
 */
const ROT_LAMBDA = 20;

/** Scratch for the north badge. Module scope: the draw path allocates nothing. */
const NORTH = { x: 0, y: 0 };
/** ...and for the mission markers' screen positions + edge clamp. */
const PT = { x: 0, y: 0 };
const EDGE = { x: 0, y: 0, edge: false, dir: 0 };

export default function Minimap({ engine, size = 220, scale = 1.4 }) {
  const canvasRef = useRef(null);
  const feed = useBlipFeed(engine);
  /** Reused every draw so the frame path allocates nothing. */
  const view = useRef({ cx: 0, cz: 0, scale, w: size, h: size, rot: 0 });
  const mark = useRef({ x: 0, z: 0, yaw: 0, live: false });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    let raf = 0;
    let last = 0;
    let dpr = 0;
    let alive = true;

    // Start ON the camera, never at 0: a level entered at the engine's default
    // 45 degrees would otherwise spin its map up out of nowhere on mount.
    view.current.rot = mapRotation(engine?.yaw ?? 0);

    const draw = (now) => {
      raf = requestAnimationFrame(draw);
      if (!alive || now - last < REDRAW_MS) return;
      // Real seconds since the last DRAW (skipped frames never touch `last`),
      // clamped so a backgrounded tab resumes by snapping rather than sweeping.
      const dt = Math.min(0.25, (now - last) * 0.001);
      last = now;

      // Re-read the DPR every draw: dragging the window to a second monitor
      // changes it, and a stale backing store is a blurry minimap forever.
      const ratio = Math.min(3, window.devicePixelRatio || 1);
      if (ratio !== dpr) {
        dpr = ratio;
        canvas.width = Math.round(size * dpr);
        canvas.height = Math.round(size * dpr);
      }
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const me = resolvePlayerMark(engine, feed.current, mark.current);
      const v = view.current;
      v.cx = me.x;
      v.cz = me.z;
      v.scale = scale;
      v.w = size;
      v.h = size;
      v.rot = dampAngle(v.rot, mapRotation(engine?.yaw ?? 0), ROT_LAMBDA, dt);

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, size, size);

      ctx.save();
      roundRectPath(ctx, 0, 0, size, size, 14);
      ctx.clip();

      // Backdrop first and UNROTATED, so the corners the rotation sweeps
      // through are already painted. A flat fill looks the same either way.
      ctx.fillStyle = MAP_COLORS.ocean;
      ctx.fillRect(0, 0, size, size);

      ctx.save();
      applyViewRotation(ctx, v);

      const base = getBase(engine?.level?.map);
      if (base) {
        blitBase(ctx, base, v);
      } else {
        // The grid is world-aligned, so it must live inside the rotation — and
        // therefore has to cover the window's circumcircle. Both the rect and
        // the scroll phase carry the pad, or the grid would jump by `pad` the
        // first time the rotation crossed zero.
        const pad = Math.ceil(size * (ROT_OVERSCAN - 1) * 0.5);
        ctx.translate(-pad, -pad);
        drawPlaceholder(ctx, size + pad * 2, size + pad * 2, {
          spacing: 22,
          offsetX: -me.x * scale + pad,
          offsetY: -me.z * scale + pad,
        });
        ctx.translate(pad, pad);
      }

      if (me.live) {
        const f = feed.current;
        for (let i = 0; i < f.count; i++) {
          const bx = viewX(v, f.x[i]);
          const by = viewY(v, f.z[i]);
          // Exact box while north-up, half-diagonal once turning — `mapRender`
          // owns that difference so both consumers cull the same way.
          if (!inViewWindow(v, bx, by, 6)) continue;
          // A blip is a disc: it needs no counter-rotation, and drawing it
          // inside the transform is what puts it in the right place.
          drawBlip(ctx, bx, by, f.kind[i], 3.2);
        }
      }

      // Inside the rotation on purpose: `PI - yaw` is world-absolute, and the
      // context turns it into "heading relative to the camera" at no cost.
      drawPlayerMarker(ctx, size * 0.5, size * 0.5, me.yaw, 11);

      ctx.restore(); // the rotation
      ctx.restore(); // the clip

      // MISSION MARKERS, drawn OUTSIDE the rotation and after the clip is
      // released — deliberately, and it is the opposite call from the blips.
      // A blip is a dot that belongs on the map and is clipped away when it
      // leaves it; a marker is a piece of HUD that must never disappear, so it
      // is CLAMPED to the rim instead (see `mapRender.js#clampToEdge`) and
      // carries an upright letter, which a rotated context would print upside
      // down half the time. `viewPoint` is the analytic twin of the transform
      // we just left, so the un-clamped ones land exactly where the rotation
      // would have put them.
      drawMarkers(ctx, feed.current, v, size);

      drawChrome(ctx, size, v.rot);
    };

    raf = requestAnimationFrame(draw);
    return () => {
      alive = false;
      cancelAnimationFrame(raf);
    };
  }, [engine, size, scale, feed]);

  return (
    <canvas
      ref={canvasRef}
      // Inline box, NOT a class: the global `canvas { width: 100% }` rule in
      // styles.css belongs to the engine's canvas, and an inline style is the
      // only thing that reliably outranks a stylesheet rule.
      style={{ width: `${size}px`, height: `${size}px` }}
      className="select-none drop-shadow-[0_6px_18px_rgba(0,0,0,0.55)]"
      aria-hidden="true"
    />
  );
}

/**
 * Every mission marker the feed carries, clamped to the window's rim when it is
 * off the map. Drawn after the clip is released, so a clamped marker sits ON
 * the bezel rather than being cut in half by it.
 *
 * ⚠ The clamp works in FINAL SCREEN px, which is why this goes through
 * {@link viewPoint} rather than `viewX`/`viewY`: an edge is a property of the
 * window, and the window does not rotate with the map.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {import('../hooks/useBlipFeed.js').BlipFeed} feed
 * @param {import('./mapRender.js').MapView} v
 * @param {number} size
 */
function drawMarkers(ctx, feed, v, size) {
  const n = feed?.markerCount ?? 0;
  if (!n) return;
  for (let i = 0; i < n; i++) {
    const mk = feed.markers[i];
    viewPoint(v, mk.x, mk.z, PT);
    clampToEdge(PT.x, PT.y, size, size, 13, EDGE);
    drawMissionMarker(ctx, EDGE.x, EDGE.y, mk, 5);
    if (EDGE.edge) {
      drawEdgeArrow(ctx, EDGE.x, EDGE.y, EDGE.dir, MARKER_COLORS[mk.kind] ?? MARKER_COLORS.objective, 4);
    } else {
      // A label only fits, and only makes sense, on a marker that is actually
      // on the map — a clamped one already has an arrow saying which way.
      drawMarkerLabel(ctx, EDGE.x, EDGE.y, mk, 9);
    }
  }
}

/**
 * The rim: bezel plus an ORBITING north badge. Drawn entirely outside the map's
 * transform — the frame is the one part of a rotating minimap that must not
 * move, and canvas text inside a rotation is text upside down half the time.
 *
 * The old camera-forward tick lived here and is gone: the map is now aligned to
 * the camera, so that tick could only ever sit at the top. North is what the
 * player can no longer read off a fixed frame, so north is what the rim shows —
 * a tick on the inscribed circle plus an UPRIGHT letter just inside it, both
 * carried round on `northScreenDir`.
 *
 * The orbit is the INSCRIBED CIRCLE, not the frame: the tick reaches 108 px of
 * a 110 px half-edge on the cardinals (so it meets the bezel) and floats inside
 * the corners, where the rounded rect is ~150 px out. Walking the rounded rect
 * instead would glue it to the frame all the way round and put the tick's point
 * OUTSIDE the bezel at 45 degrees — the chrome is drawn after the clip is
 * released, so that would be a stray triangle on the HUD, not a clipped one.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} size
 * @param {number} rot Live map rotation, radians (already damped).
 */
function drawChrome(ctx, size, rot) {
  const half = size * 0.5;
  const n = northScreenDir(rot, NORTH);

  // Bezel: two strokes, the inner one dark, so it reads on any background.
  ctx.lineWidth = 2;
  ctx.strokeStyle = MAP_COLORS.rim;
  roundRectPath(ctx, 1, 1, size - 2, size - 2, 13);
  ctx.stroke();
  ctx.lineWidth = 1;
  ctx.strokeStyle = 'rgba(0,0,0,0.6)';
  roundRectPath(ctx, 2.5, 2.5, size - 5, size - 5, 11.5);
  ctx.stroke();

  // Tick, pointing OUT along the bearing. A triangle is symmetric about its
  // axis, so rotating it is not the same sin as rotating a letter.
  const tr = half - 6;
  ctx.save();
  ctx.translate(half + n.x * tr, half + n.y * tr);
  ctx.rotate(Math.atan2(n.y, n.x));
  ctx.beginPath();
  ctx.moveTo(4, 0);
  ctx.lineTo(-3, -3.4);
  ctx.lineTo(-3, 3.4);
  ctx.closePath();
  ctx.fillStyle = MAP_COLORS.north;
  ctx.globalAlpha = 0.85;
  ctx.fill();
  ctx.restore();

  // The letter: orbits with the tick, never turns.
  const lr = half - 18;
  const lx = half + n.x * lr;
  const ly = half + n.y * lr;
  ctx.beginPath();
  ctx.arc(lx, ly, 8, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(7,8,12,0.75)';
  ctx.fill();
  ctx.font = '700 11px "Barlow Condensed", system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = MAP_COLORS.north;
  ctx.fillText('N', lx, ly);
}
